import React, {useCallback, useContext} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {HotelCardContext} from 'components/HotelCardContext'
import {usePropertyHighlightsAnalyticsPayload} from 'components/PropertyHighlights'
import {useHotelCardActions} from 'hooks/useHotelCardActions'
import {useOfferClickHandler} from 'hooks/useOfferClickHandler'
import useRedirectLink from 'hooks/useRedirectLink'
import {useShowAccommodationPage} from 'hooks/useShowAccommodationPage'
import {getHotelTopOffer, getOfferEntity} from 'modules/sapiSearch/selectors'
import {getClickType, isTrackableAlternativeClick} from 'utils/clickType'
import {SelectedElement} from 'utils/selectedElement'

import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {getClosestDataIdToEventTarget} from '@vio/core/src/_web/utils/browser'
import {logTimeStart} from '@vio/core/src/_web/utils/logging/performanceTimer'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  ClickType,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {isAirbnbOffer} from '@vio/core/src/offer/business/isAirbnbOffer'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {useOtaMode} from '../../../hooks/useOtaMode'

interface Props {
  children: React.ReactElement
  trackingComponent?: 'ListViewHotelCard' | 'MapViewHotelCard'
}

/**
 * Wrapper component which creates a `<a>` link to the Airbnb Accommodation Page
 * This is meant to be used internally on the hotel cards only
 */
const AirbnbAccommodationLink = ({
  hotelId,
  topOffer,
  redirectUrl,
  children,
  onAnchorClick,
  onContextMenu,
  onAuxClick
}) => {
  const {handleClick: handleAirbnbClick} = useOfferClickHandler({
    component: 'HotelCardNonOffer',
    hotelId,
    offer: topOffer,
    offerPosition: 0
  })
  return (
    <NoStyleLink
      href={redirectUrl}
      rel="noopener noreferrer"
      target="_blank"
      tabIndex={0}
      onClick={e => {
        onAnchorClick(e)
        handleAirbnbClick(e)
      }}
      onContextMenu={onContextMenu}
      onAuxClick={onAuxClick}
    >
      {children}
    </NoStyleLink>
  )
}

/**
 * Wrapper component which creates a `<a>` link to the Accommodation Page
 * This is meant to be used internally on the hotel cards only
 */
export const AccommodationPageLink = ({
  children,
  trackingComponent = 'ListViewHotelCard'
}: Props) => {
  const {hotelId} = useContext(HotelCardContext)
  // Added as part of dcbc5616-property-highlights-v1
  const {highlights: displayedHighlights} =
    usePropertyHighlightsAnalyticsPayload(hotelId)
  const {trackUserHotelCardClicked} = useHotelCardActions()
  const {getAccommodationPageUrl} = useShowAccommodationPage()
  const accommodationPageURL = getAccommodationPageUrl(hotelId)
  const {isOTAMode} = useOtaMode()
  const topOffer = useSelector(state => getHotelTopOffer(state, hotelId))
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const anchorPriceTotal = offerEntity?.anchorPriceRateBreakdown?.totalRate ?? 0
  const hydrated = useIsHydrated()
  const redirectUrl = useRedirectLink(
    {offer: topOffer, hotelId, anchorPriceTotal},
    hydrated
  )
  const shouldRedirectToAirbnb = isAirbnbOffer(topOffer) && Boolean(redirectUrl)
  const dispatch = useDispatch()

  const isMapView = trackingComponent === 'MapViewHotelCard'

  const handleOnContextMenu = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.ContextMenu,
        action: Action.Displayed,
        component: 'ListViewHotelCard'
      })
    )
  }, [dispatch])

  // Filter HeroImage and HeroImageCount as payload and pass Image instead
  const getSelectedElement = (e: React.MouseEvent<HTMLElement>) => {
    const dataId = getClosestDataIdToEventTarget(e)
    if (dataId) {
      return ['HeroImage', 'HeroImageCount'].includes(dataId)
        ? SelectedElement.Image
        : dataId
    }

    return SelectedElement.BlankSpace
  }

  // The map card uses this link and shows no highlights.
  // Added as part of dcbc5616-property-highlights-v1
  const highlights = isMapView ? undefined : displayedHighlights

  const handleHotelCardClickTracking = useCallback(
    (e: React.MouseEvent<HTMLElement>, clickType = ClickType.LeftClick) => {
      trackUserHotelCardClicked({
        hotelId: hotelId,
        component: trackingComponent,
        selectedElement: getSelectedElement(e),
        highlights,
        clickType
      })
      logTimeStart('hotelCardClick')
    },
    [highlights, hotelId, trackUserHotelCardClicked, trackingComponent]
  )

  /**
   * Right, middle and long-press clicks open the card in a new tab without ever
   * firing a plain `click`, so the card click goes untracked without these.
   * Only wired on the branches that render a real link.
   */
  const handleAlternativeClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!isTrackableAlternativeClick(e)) return
      if (e.type === 'contextmenu') handleOnContextMenu()

      handleHotelCardClickTracking(e, getClickType(e))
    },
    [handleHotelCardClickTracking, handleOnContextMenu]
  )

  if (shouldRedirectToAirbnb) {
    return (
      <AirbnbAccommodationLink
        hotelId={hotelId}
        topOffer={topOffer}
        redirectUrl={redirectUrl}
        onAnchorClick={handleHotelCardClickTracking}
        onContextMenu={handleAlternativeClick}
        onAuxClick={handleAlternativeClick}
      >
        {children}
      </AirbnbAccommodationLink>
    )
  }

  return (
    <NoStyleLink
      href={isOTAMode && redirectUrl ? redirectUrl : accommodationPageURL}
      rel="noopener noreferrer"
      target="_blank"
      tabIndex={0}
      onClick={handleHotelCardClickTracking}
      onContextMenu={handleAlternativeClick}
      onAuxClick={handleAlternativeClick}
    >
      {children}
    </NoStyleLink>
  )
}
